import { Box, Checkbox, Divider, FormControlLabel, Grid, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, makeStyles } from '@material-ui/core';
import { DeleteOutline } from '@material-ui/icons';
import { DataGridPro, GridColDef, GridSelectionModel } from '@mui/x-data-grid-pro';
import { InvestidorVendaResponse, CustodianteInvestidorResponse, TransferenciaLoteResponse, TransferenciaDocumentoLoteResponse } from 'common/genericTypes';
import { transferenciaTipoOptions } from 'common/genericOptions';
import UploadDocs, { Attachment } from 'components/uploadDocs';
import { AutoComplete, Button, Dropdown, MdSpace, NumberField, Section, SmSpace, TextField } from 'm2m.components';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { RootState } from 'store';
import { addAlert } from 'store/modules/snackBar';
import TransferenciaFeedbackModal from './transferenciaFeedbackModal';
import {
  GETInvestidoresVenda,
  GETInstrumentosInvestidorVenda,
  GETCustodianteVenda,
  GETCustodiantesInvestidor,
  POSTTransferenciaCustodiaLote,
  POSTTransferenciaTitularidadeLote,
  POSTTransferenciaDocumentosLote,
} from 'requests/operacao';
import { CpfCnpjMask } from 'components/masks';
import { localizedTextsMap } from 'utils/dataGrid/dataGridUtils';
import { addCpfCnpjMask } from 'utils/mask';
import { formatIntNumber } from 'utils/number';
import { validaCpfCnpj, stripCpfCnpjFormatting } from 'utils/validation';
import { v4 as uuid } from 'uuid';

interface InstrumentoDisponivel {
  id: string;
  codigo: string;
  serie: string;
  vencimento: string;
  saldoDisponivel: number;
}

interface DestinoTransferencia {
  id: string;
  custodianteCnpj: string;
  custodianteLabel: string;
  custodianteErro: string;
  investidorCpfCnpj: string;
  investidorLabel: string;
  investidorErro: string;
  quantidade: number;
}

interface IFCarrinho extends InstrumentoDisponivel {
  destinos: DestinoTransferencia[];
  transferirTotal: boolean;
}

const useStyles = makeStyles((theme) => ({
  wideForm: {
    '& .MuiGrid-spacing-xs-4 > .MuiGrid-grid-lg-6': {
      flexBasis: '70%',
      maxWidth: '70%',
    },
    '& .MuiGrid-spacing-xs-4 > .MuiGrid-grid-lg-3': {
      flexBasis: '20%',
      maxWidth: '20%',
    },
    [theme.breakpoints.down('md')]: {
      '& .MuiGrid-spacing-xs-4 > .MuiGrid-grid-lg-6': {
        flexBasis: '100%',
        maxWidth: '100%',
      },
      '& .MuiGrid-spacing-xs-4 > .MuiGrid-grid-lg-3': {
        flexBasis: '100%',
        maxWidth: '100%',
      },
    },
  },
}));

const TransferenciaUnificada: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const usuarioLogado = useSelector((state: RootState) => state.auth.usuario);

  const [tipoTransferencia, setTipoTransferencia] = useState<string>('');
  const [investidores, setInvestidores] = useState<InvestidorVendaResponse[]>([]);
  const [investidorSelecionado, setInvestidorSelecionado] = useState<InvestidorVendaResponse | null>(null);
  const [instrumentosDisponiveis, setInstrumentosDisponiveis] = useState<InstrumentoDisponivel[]>([]);
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
  const [pageSize, setPageSize] = useState(5);
  const [ifsCarrinho, setIfsCarrinho] = useState<IFCarrinho[]>([]);
  const [anexos, setAnexos] = useState<Attachment[]>([]);
  const [uploadKey, setUploadKey] = useState(0);
  const [transferirTotalGlobal, setTransferirTotalGlobal] = useState(false);
  const [custodiaPropria, setCustodiaPropria] = useState(false);
  const [custodianteDestinoCnpj, setCustodianteDestinoCnpj] = useState('');
  const [custodianteDestinoLabel, setCustodianteDestinoLabel] = useState('');
  const [custodianteDestinoErro, setCustodianteDestinoErro] = useState('');
  const [custodiantesDestinoOptions, setCustodiantesDestinoOptions] = useState<CustodianteInvestidorResponse[]>([]);
  const [custodianteDestinoSelecionado, setCustodianteDestinoSelecionado] = useState<CustodianteInvestidorResponse | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackData, setFeedbackData] = useState<TransferenciaLoteResponse | null>(null);
  const [documentosFeedback, setDocumentosFeedback] = useState<TransferenciaDocumentoLoteResponse | null>(null);

  const history = useHistory();

  const resetForm = () => {
    setInvestidorSelecionado(null);
    setInstrumentosDisponiveis([]);
    setSelectionModel([]);
    setIfsCarrinho([]);
    setAnexos([]);
    setUploadKey((prev) => prev + 1);
    setTransferirTotalGlobal(false);
    setCustodiaPropria(false);
    setCustodianteDestinoCnpj('');
    setCustodianteDestinoLabel('');
    setCustodianteDestinoErro('');
    setCustodiantesDestinoOptions([]);
    setCustodianteDestinoSelecionado(null);
  };

  const handleTipoTransferenciaChange = (value: string) => {
    setTipoTransferencia(value);
    resetForm();
  };

  const isCustodia = tipoTransferencia === 'CUSTODIA';
  const isTitularidade = tipoTransferencia === 'TITULARIDADE';

  const custodianteLabel = usuarioLogado?.participante
    ? `${usuarioLogado.participante.razaoSocial} - ${addCpfCnpjMask(usuarioLogado.participante.cnpj)}`
    : '';

  const ifsColumns: GridColDef[] = [
    { field: 'codigo', headerName: 'Código', flex: 1 },
    {
      field: 'saldoDisponivel',
      headerName: 'Quantidade Disponível',
      flex: 1,
      valueFormatter: ({ value }) => formatIntNumber(value as number),
    },
  ];

  const fetchInvestidores = useCallback(async () => {
    try {
      const result = await GETInvestidoresVenda(dispatch);
      setInvestidores(result);
    } catch {}
  }, [dispatch]);

  useEffect(() => {
    fetchInvestidores();
  }, [fetchInvestidores]);

  useEffect(() => {
    if (!investidorSelecionado) {
      setInstrumentosDisponiveis([]);
      return;
    }

    const cpfCnpjLimpo = stripCpfCnpjFormatting(investidorSelecionado.cpfCnpj);
    let cancelled = false;

    const fetchInstrumentos = async () => {
      try {
        const result = await GETInstrumentosInvestidorVenda(dispatch, cpfCnpjLimpo, true);
        if (cancelled) return;

        const lista = Array.isArray(result) ? result : (result as any)?.instrumentos ?? [];

        setInstrumentosDisponiveis(
          lista.map((inst: any, index: number) => ({
            id: inst.codigo ?? `inst-${index}`,
            codigo: inst.codigo ?? '',
            serie: inst.serie,
            vencimento: new Date(inst.dataVencimento).toLocaleDateString('pt-BR'),
            saldoDisponivel: inst.quantidade,
          }))
        );
      } catch (error: any) {
        if (cancelled) return;
        const msg =
          error?.response?.data?.message ??
          error?.response?.data?.errors?.join(', ') ??
          'Erro ao buscar instrumentos financeiros do investidor.';
        dispatch(addAlert({ severity: 'error', message: msg }));
        setInstrumentosDisponiveis([]);
      }
    };

    fetchInstrumentos();

    return () => {
      cancelled = true;
    };
  }, [investidorSelecionado, dispatch]);

  const handleInvestidorChange = (value: InvestidorVendaResponse | null) => {
    setInvestidorSelecionado(value);
    setInstrumentosDisponiveis([]);
    setSelectionModel([]);
    setCustodiantesDestinoOptions([]);
    setCustodianteDestinoSelecionado(null);
    if (ifsCarrinho.length > 0) {
      setIfsCarrinho([]);
    }

    if (value && isCustodia) {
      const cpfCnpjLimpo = stripCpfCnpjFormatting(value.cpfCnpj);
      GETCustodiantesInvestidor(dispatch, cpfCnpjLimpo, true)
        .then((result) => setCustodiantesDestinoOptions(result))
        .catch(() => setCustodiantesDestinoOptions([]));
    }
  };

  const handleAdicionarSelecionados = () => {
    const novosIFs: IFCarrinho[] = instrumentosDisponiveis
      .filter((item) => selectionModel.includes(item.id))
      .filter((item) => !ifsCarrinho.some((c) => c.id === item.id))
      .map((item) => ({
        ...item,
        transferirTotal: isCustodia && transferirTotalGlobal,
        destinos: isCustodia
          ? [{ id: uuid(), custodianteCnpj: '', custodianteLabel: '', custodianteErro: '', investidorCpfCnpj: '', investidorLabel: '', investidorErro: '', quantidade: transferirTotalGlobal ? item.saldoDisponivel : 0 }]
          : [],
      }));
    if (novosIFs.length > 0) {
      setIfsCarrinho((prev) => [...prev, ...novosIFs]);
    }
  };

  const handleRemoverIF = (ifId: string) => {
    setIfsCarrinho((prev) => prev.filter((item) => item.id !== ifId));
  };

  const handleAdicionarInvestidor = (ifId: string) => {
    const cnpj = isTitularidade && custodiaPropria && usuarioLogado?.participante
      ? usuarioLogado.participante.cnpj : '';
    const label = isTitularidade && custodiaPropria && usuarioLogado?.participante
      ? `${usuarioLogado.participante.razaoSocial} - ${addCpfCnpjMask(usuarioLogado.participante.cnpj)}`
      : '';

    const destinoId = uuid();
    const novoDestino: DestinoTransferencia = {
      id: destinoId,
      custodianteCnpj: cnpj,
      custodianteLabel: label,
      custodianteErro: '',
      investidorCpfCnpj: '',
      investidorLabel: '',
      investidorErro: '',
      quantidade: 0,
    };
    setIfsCarrinho((prev) =>
      prev.map((item) => (item.id === ifId ? { ...item, destinos: [...item.destinos, novoDestino] } : item))
    );
  };

  const handleRemoverDestino = (ifId: string, destinoId: string) => {
    setIfsCarrinho((prev) =>
      prev.map((item) =>
        item.id === ifId ? { ...item, destinos: item.destinos.filter((d) => d.id !== destinoId) } : item
      )
    );
  };

  const handleUpdateDestino = (ifId: string, destinoId: string, updates: Partial<DestinoTransferencia>) => {
    setIfsCarrinho((prev) =>
      prev.map((item) =>
        item.id === ifId
          ? { ...item, destinos: item.destinos.map((d) => (d.id === destinoId ? { ...d, ...updates } : d)) }
          : item
      )
    );
  };

  const handleQuantidadeManualChange = (ifId: string, destinoId: string, value: number) => {
    setIfsCarrinho((prev) =>
      prev.map((item) => {
        if (item.id !== ifId) return item;
        const updatedDestinos = item.destinos.map((d) => (d.id === destinoId ? { ...d, quantidade: value } : d));
        const shouldUncheck = item.transferirTotal && value !== item.saldoDisponivel;
        return { ...item, destinos: updatedDestinos, transferirTotal: shouldUncheck ? false : item.transferirTotal };
      })
    );
    const allStillTotal = ifsCarrinho.every((item) =>
      item.id === ifId ? (value === item.saldoDisponivel && item.transferirTotal) : item.transferirTotal
    );
    if (transferirTotalGlobal && !allStillTotal) {
      setTransferirTotalGlobal(false);
    }
  };

  const handleCustodianteCnpjChange = (ifId: string, destinoId: string, value: string) => {
    const masked = CpfCnpjMask(value ?? '');
    handleUpdateDestino(ifId, destinoId, { custodianteCnpj: masked, custodianteLabel: '', custodianteErro: '', investidorCpfCnpj: '', investidorLabel: '', investidorErro: '' });

    const cnpjLimpo = stripCpfCnpjFormatting(masked);
    if (cnpjLimpo.length === 14 && validaCpfCnpj(masked)) {
      GETCustodianteVenda(dispatch, cnpjLimpo)
        .then((result) => {
          handleUpdateDestino(ifId, destinoId, {
            custodianteLabel: `${result.razaoSocial} - ${addCpfCnpjMask(cnpjLimpo)}`,
            custodianteErro: '',
          });
        })
        .catch((error: any) => {
          const msg =
            error?.response?.data?.message ??
            error?.response?.data?.errors?.join(', ') ??
            'Erro ao buscar custodiante.';
          handleUpdateDestino(ifId, destinoId, { custodianteLabel: '', custodianteErro: msg });
        });
    } else if (cnpjLimpo.length >= 14) {
      handleUpdateDestino(ifId, destinoId, { custodianteErro: 'CNPJ inválido' });
    }
  };

  const handleInvestidorDestinoCpfCnpjChange = (ifId: string, destinoId: string, value: string) => {
    const masked = CpfCnpjMask(value ?? '');
    handleUpdateDestino(ifId, destinoId, { investidorCpfCnpj: masked, investidorLabel: '', investidorErro: '' });
  };

  const calcAlocado = (destinos: DestinoTransferencia[]) => destinos.reduce((sum, d) => sum + d.quantidade, 0);

  const calcRestante = (item: IFCarrinho) => item.saldoDisponivel - calcAlocado(item.destinos);

  const handleTransferirTotalChange = (ifId: string, checked: boolean) => {
    setIfsCarrinho((prev) =>
      prev.map((item) => {
        if (item.id !== ifId) return item;
        if (checked && item.destinos.length === 1) {
          return {
            ...item,
            transferirTotal: true,
            destinos: [{ ...item.destinos[0], quantidade: item.saldoDisponivel }],
          };
        }
        return { ...item, transferirTotal: false, destinos: item.destinos.map((d) => ({ ...d, quantidade: 0 })) };
      })
    );
  };

  const handleTransferirTotalGlobalChange = (checked: boolean) => {
    setTransferirTotalGlobal(checked);
    if (checked) {
      setIfsCarrinho((prev) =>
        prev.map((item) => {
          const destino = item.destinos.length === 1
            ? { ...item.destinos[0], quantidade: item.saldoDisponivel }
            : item.destinos.length === 0
              ? {
                  id: uuid(),
                  custodianteCnpj: '',
                  custodianteLabel: '',
                  custodianteErro: '',
                  investidorCpfCnpj: '',
                  investidorLabel: '',
                  investidorErro: '',
                  quantidade: item.saldoDisponivel,
                }
              : item.destinos[0];
          return { ...item, transferirTotal: true, destinos: [destino] };
        })
      );
    } else {
      setIfsCarrinho((prev) =>
        prev.map((item) => ({
          ...item,
          transferirTotal: false,
          destinos: item.destinos.map((d) => ({ ...d, quantidade: 0 })),
        }))
      );
    }
  };

  const handleCustodiaPropriaChange = (checked: boolean) => {
    setCustodiaPropria(checked);
    if (checked && usuarioLogado?.participante) {
      const cnpj = usuarioLogado.participante.cnpj;
      const label = `${usuarioLogado.participante.razaoSocial} - ${addCpfCnpjMask(cnpj)}`;
      setIfsCarrinho((prev) =>
        prev.map((item) => ({
          ...item,
          destinos: item.destinos.map((d) => ({
            ...d,
            custodianteCnpj: cnpj,
            custodianteLabel: label,
            custodianteErro: '',
          })),
        }))
      );
    } else {
      setIfsCarrinho((prev) =>
        prev.map((item) => ({
          ...item,
          destinos: item.destinos.map((d) => ({
            ...d,
            custodianteCnpj: '',
            custodianteLabel: '',
            custodianteErro: '',
          })),
        }))
      );
    }
  };

  const handleCustodianteDestinoCnpjChange = (value: string) => {
    const masked = CpfCnpjMask(value ?? '');
    setCustodianteDestinoCnpj(masked);
    setCustodianteDestinoLabel('');
    setCustodianteDestinoErro('');

    const cnpjLimpo = stripCpfCnpjFormatting(masked);
    if (cnpjLimpo.length === 14 && validaCpfCnpj(masked)) {
      GETCustodianteVenda(dispatch, cnpjLimpo)
        .then((result) => {
          setCustodianteDestinoLabel(`${result.razaoSocial} - ${addCpfCnpjMask(cnpjLimpo)}`);
          setCustodianteDestinoErro('');
        })
        .catch((error: any) => {
          const msg =
            error?.response?.data?.message ??
            error?.response?.data?.errors?.join(', ') ??
            'Erro ao buscar custodiante.';
          setCustodianteDestinoLabel('');
          setCustodianteDestinoErro(msg);
        });
    } else if (cnpjLimpo.length >= 14) {
      setCustodianteDestinoErro('CNPJ inválido');
    }
  };

  const uploadDocumentos = async (transferenciaResult: TransferenciaLoteResponse) => {
    const arquivos = anexos.map((a) => a.file).filter((f): f is File => !!f);
    if (arquivos.length === 0) return;

    const operacaoIds = transferenciaResult.itens
      .filter((item) => item.sucesso && item.operacaoId)
      .map((item) => item.operacaoId!);

    if (operacaoIds.length === 0) return;

    try {
      const docResult = await POSTTransferenciaDocumentosLote(dispatch, operacaoIds, arquivos, true);
      setDocumentosFeedback(docResult);
    } catch {
      setDocumentosFeedback(null);
    }
  };

  const handleSubmit = async () => {
    if (!investidorSelecionado) return;
    setSubmitting(true);

    try {
      const cpfCnpjOrigem = stripCpfCnpjFormatting(investidorSelecionado.cpfCnpj);

      if (isCustodia) {
        if (!custodianteDestinoSelecionado) return;
        const payload = {
          investidorOrigemCpfCnpj: cpfCnpjOrigem,
          custodianteDestinoCnpj: stripCpfCnpjFormatting(custodianteDestinoSelecionado.cnpj),
          instrumentosFinanceiros: ifsCarrinho.map((item) => ({
            codigo: item.codigo,
            quantidade: item.destinos[0]?.quantidade ?? 0,
          })),
        };
        const result = await POSTTransferenciaCustodiaLote(dispatch, payload, true);
        await uploadDocumentos(result);
        setFeedbackData(result);
        setFeedbackOpen(true);
        resetForm();
      }

      if (isTitularidade) {
        const payload = {
          investidorOrigemCpfCnpj: cpfCnpjOrigem,
          instrumentosFinanceiros: ifsCarrinho.map((item) => ({
            codigo: item.codigo,
            destinos: item.destinos.map((d) => ({
              custodianteDestinoCnpj: custodiaPropria ? null : (stripCpfCnpjFormatting(d.custodianteCnpj) || null),
              investidorDestinoCpfCnpj: stripCpfCnpjFormatting(d.investidorCpfCnpj),
              quantidade: d.quantidade,
            })),
          })),
        };
        const result = await POSTTransferenciaTitularidadeLote(dispatch, payload, true);
        await uploadDocumentos(result);
        setFeedbackData(result);
        setFeedbackOpen(true);
        resetForm();
      }
    } catch (error: any) {
      const responseData = error?.response?.data;
      if (responseData?.status && responseData?.resumo && responseData?.itens) {
        setFeedbackData(responseData as TransferenciaLoteResponse);
        setFeedbackOpen(true);
        resetForm();
      } else {
        const msg =
          responseData?.message ??
          responseData?.errors?.join(', ') ??
          'Erro ao processar transferência.';
        dispatch(addAlert({ severity: 'error', message: msg }));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box p={3} className={classes.wideForm}>
      <Typography variant="h1">{'Transferência'}</Typography>

      <Section title="Detalhes da movimentação" defaultGrid={false}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField label="Custodiante Origem" value={custodianteLabel} disabled />
          </Grid>
          <Grid item xs={12} md={6}>
            <Dropdown
              label="Tipo de Transferência"
              options={transferenciaTipoOptions}
              value={tipoTransferencia}
              onChange={handleTipoTransferenciaChange}
              required
            />
          </Grid>
        </Grid>
      </Section>

      {(isCustodia || isTitularidade) && (
      <>
      <Section title="Origem" defaultGrid={false}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <AutoComplete
              label="Investidor Origem"
              options={investidores}
              getOptionLabel={(option: InvestidorVendaResponse | null) => {
                if (!option) return '';
                return `${option.razaoSocial} - ${addCpfCnpjMask(option.cpfCnpj)}`;
              }}
              value={investidorSelecionado}
              onChange={(value: InvestidorVendaResponse | null) => handleInvestidorChange(value)}
            />
          </Grid>
        </Grid>
      </Section>

      <Section title="IFs Disponíveis" defaultGrid={false}>
        <DataGridPro
          autoHeight
          localeText={localizedTextsMap}
          rows={instrumentosDisponiveis}
          columns={ifsColumns}
          checkboxSelection
          disableSelectionOnClick
          pageSize={pageSize}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          rowsPerPageOptions={[5, 10, 20]}
          pagination
          selectionModel={selectionModel}
          onSelectionModelChange={(newModel) => setSelectionModel(newModel)}
        />
        <SmSpace />
        <Grid container justify="flex-end">
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              disabled={selectionModel.length === 0}
              onClick={handleAdicionarSelecionados}
            >
              {'Adicionar selecionados'}
            </Button>
          </Grid>
        </Grid>
        <MdSpace />
      </Section>

      {isCustodia && (
        <Section title="Destino" defaultGrid={false}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <AutoComplete
                label="Custodiante Destino"
                options={custodiantesDestinoOptions}
                getOptionLabel={(option: CustodianteInvestidorResponse | null) => {
                  if (!option) return '';
                  return `${option.razaoSocial} - ${addCpfCnpjMask(option.cnpj)}`;
                }}
                value={custodianteDestinoSelecionado}
                onChange={(value: CustodianteInvestidorResponse | null) => setCustodianteDestinoSelecionado(value)}
                disabled={custodiantesDestinoOptions.length === 0}
              />
            </Grid>
          </Grid>
        </Section>
      )}

      <Section title="Carrinho de IFs" defaultGrid={false}>
        {isCustodia && (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={ifsCarrinho.length > 0 && ifsCarrinho.every((item) => item.transferirTotal)}
                  indeterminate={ifsCarrinho.some((item) => item.transferirTotal) && !ifsCarrinho.every((item) => item.transferirTotal)}
                  onChange={(e) => handleTransferirTotalGlobalChange(e.target.checked)}
                  color="primary"
                />
              }
              label="Transferir total"
            />
            <MdSpace />
          </>
        )}
        {isTitularidade && (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={custodiaPropria}
                  onChange={(e) => handleCustodiaPropriaChange(e.target.checked)}
                  color="primary"
                />
              }
              label="A custódia é própria?"
            />
            <MdSpace />
          </>
        )}
        {ifsCarrinho.length === 0 ? (
          <Box py={3} textAlign="center">
            <Typography variant="body2" color="textSecondary">
              {'Nenhum IF adicionado ao carrinho.'}
            </Typography>
          </Box>
        ) : (
          ifsCarrinho.map((item) => (
            <Paper key={item.id} elevation={2} style={{ padding: '16px', marginBottom: '16px' }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box display="flex" style={{ gap: '32px' }}>
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      {'Código'}
                    </Typography>
                    <Typography variant="body1">
                      <strong>{item.codigo}</strong>
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      {'Série'}
                    </Typography>
                    <Typography variant="body1">
                      <strong>{item.serie}</strong>
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      {'Vencimento'}
                    </Typography>
                    <Typography variant="body1">
                      <strong>{item.vencimento}</strong>
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      {'Saldo'}
                    </Typography>
                    <Typography variant="body1">
                      <strong>{formatIntNumber(item.saldoDisponivel)}</strong>
                    </Typography>
                  </Box>
                </Box>
                <Box display="flex" alignItems="center" style={{ gap: '24px' }}>
                  <Box textAlign="right">
                    <Typography variant="caption" color="textSecondary">
                      {'Alocado'}
                    </Typography>
                    <Typography variant="body1">
                      <strong>{formatIntNumber(calcAlocado(item.destinos))}</strong>
                    </Typography>
                  </Box>
                  <Box textAlign="right">
                    <Typography variant="caption" color="textSecondary">
                      {'Restante'}
                    </Typography>
                    <Typography variant="body1" color={calcRestante(item) < 0 ? 'error' : 'textPrimary'}>
                      <strong>{formatIntNumber(calcRestante(item))}</strong>
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={() => handleRemoverIF(item.id)}>
                    <DeleteOutline />
                  </IconButton>
                </Box>
              </Box>

              <Box mt={2} mb={2}>
                <Divider />
              </Box>

              {calcRestante(item) < 0 && (
                <Box
                  mb={2}
                  p={1}
                  style={{ backgroundColor: '#fdecea', border: '1px solid #f44336', borderRadius: '4px' }}
                >
                  <Typography variant="body2" color="error">
                    {`Quantidade alocada (${formatIntNumber(calcAlocado(item.destinos))}) ultrapassa o saldo disponível (${formatIntNumber(item.saldoDisponivel)}). Revise os investidores.`}
                  </Typography>
                </Box>
              )}

              {item.destinos.length === 0 ? (
                <Box py={2} textAlign="center">
                  <Typography variant="body2" color="textSecondary">
                    {'Adicione pelo menos um destino para este IF.'}
                  </Typography>
                </Box>
              ) : (
                item.destinos.map((destino, destinoIndex) => (
                  isCustodia ? (
                    <Box key={destino.id}>
                      <Grid container spacing={3} alignItems="center">
                        <Grid item xs={12} md={6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={item.transferirTotal}
                                onChange={(e) => handleTransferirTotalChange(item.id, e.target.checked)}
                                color="primary"
                              />
                            }
                            label="Transferir total"
                          />
                        </Grid>
                      </Grid>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <NumberField
                            label="Quantidade"
                            value={destino.quantidade}
                            onChange={(value) => handleQuantidadeManualChange(item.id, destino.id, value ?? 0)}
                            numberType="integer"
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  ) : (
                    <Box key={destino.id} mb={2} p={2} style={{ border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                      <Grid container alignItems="center" style={{ marginBottom: '8px' }}>
                        <Grid item xs>
                          <Typography variant="subtitle2">
                            <strong>{`Destino ${destinoIndex + 1}`}</strong>
                          </Typography>
                        </Grid>
                        <Grid item>
                          <IconButton size="small" onClick={() => handleRemoverDestino(item.id, destino.id)}>
                            <DeleteOutline />
                          </IconButton>
                        </Grid>
                      </Grid>

                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Custodiante Destino"
                            value={destino.custodianteLabel || destino.custodianteCnpj}
                            onChange={(value) => handleCustodianteCnpjChange(item.id, destino.id, value)}
                            placeholder="Digite o CNPJ do custodiante"
                            error={!!destino.custodianteErro}
                            errorMessage={destino.custodianteErro}
                            disabled={custodiaPropria}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={item.transferirTotal}
                                onChange={(e) => handleTransferirTotalChange(item.id, e.target.checked)}
                                color="primary"
                                disabled={item.destinos.length > 1}
                              />
                            }
                            label="Transferir total"
                          />
                        </Grid>
                      </Grid>

                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Investidor Destino (CPF/CNPJ)"
                            value={destino.investidorCpfCnpj}
                            onChange={(value) => handleInvestidorDestinoCpfCnpjChange(item.id, destino.id, value)}
                            placeholder="Digite o CPF/CNPJ do investidor"
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <NumberField
                            label="Quantidade"
                            value={destino.quantidade}
                            onChange={(value) => handleQuantidadeManualChange(item.id, destino.id, value ?? 0)}
                            numberType="integer"
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  )
                ))
              )}

              {isTitularidade && (
                <Box textAlign="center" mt={2}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => handleAdicionarInvestidor(item.id)}
                    disabled={item.transferirTotal}
                  >
                    {'Adicionar destino'}
                  </Button>
                </Box>
              )}
            </Paper>
          ))
        )}
      </Section>
      <Section title="Anexos" defaultGrid={false}>
        <Typography variant="body2" color="textSecondary">
          {'Documento global de todas as transferências (não obrigatório)'}
        </Typography>
        <SmSpace />
        <UploadDocs
          key={uploadKey}
          onChange={(atts) => setAnexos(anexos.concat(atts as Attachment[]))}
          onDelete={(attId) => setAnexos(anexos.filter((a) => a.id !== attId))}
          multipleFiles={true}
        />
      </Section>

      <Section title="Resumo da operação" defaultGrid={false}>
        {ifsCarrinho.length === 0 ? (
          <Box py={3} textAlign="center">
            <Typography variant="body2" color="textSecondary">
              {'Nenhum IF adicionado ao carrinho.'}
            </Typography>
          </Box>
        ) : (
          <>
            <Grid container spacing={2} style={{ marginBottom: '24px' }}>
              {[
                { label: 'IFs no carrinho', value: ifsCarrinho.length },
                { label: 'Total de destinos', value: ifsCarrinho.reduce((sum, item) => sum + item.destinos.length, 0) },
                {
                  label: 'Quantidade total',
                  value: formatIntNumber(ifsCarrinho.reduce((sum, item) => sum + calcAlocado(item.destinos), 0)),
                },
              ].map((card) => (
                <Grid item xs={12} sm={6} md={4} key={card.label}>
                  <Paper elevation={2} style={{ padding: '16px', textAlign: 'center' }}>
                    <Typography variant="caption" color="textSecondary">
                      {card.label}
                    </Typography>
                    <Typography variant="h5">
                      <strong>{card.value}</strong>
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>{'Código IF'}</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>{'Destinos'}</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{'Quantidade total'}</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ifsCarrinho.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.codigo}</TableCell>
                      <TableCell align="center">{item.destinos.length}</TableCell>
                      <TableCell align="right">{formatIntNumber(calcAlocado(item.destinos))}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Section>

      <Box display="flex" justifyContent="flex-end" style={{ gap: '16px' }} mt={2} mb={4}>
        <Button variant="contained" color="primary" disabled={ifsCarrinho.length === 0 || submitting} onClick={handleSubmit}>
          {submitting ? 'Enviando...' : 'Confirmar movimentação'}
        </Button>
      </Box>

      {feedbackData && (
        <TransferenciaFeedbackModal
          open={feedbackOpen}
          onClose={() => {
            setFeedbackOpen(false);
            if (feedbackData.status === 'SUCESSO') {
              history.push('/operacoes/gerenciar');
            }
          }}
          data={feedbackData}
          documentos={documentosFeedback}
        />
      )}
      </>
      )}
    </Box>
  );
};

export default TransferenciaUnificada;
